import React, { useState } from 'react';
import { Container, Box, Typography, Collapse, IconButton } from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SearchIcon from '@mui/icons-material/Search';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import SecurityIcon from '@mui/icons-material/Security';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SectionHeader from './SectionHeader';

const FAQSection = () => {
  const [expandedItems, setExpandedItems] = useState(new Set());

  const faqs = [
    {
      icon: RocketLaunchIcon,
      title: "Do I need to connect any accounts or give access to my data?",
      description: "No account connections required. We gather all data from publicly available sources only. This means you can analyze any business worldwide - including competitors, suppliers, and clients - without giving us access to your private data."
    },
    {
      icon: SearchIcon,
      title: "What makes Sashy different from other review analysis tools?",
      description: "Sashy provides quantitative analysis - not just what customers like or dislike, but exactly how customer experience issues affect your ratings and revenue. We're also 10x cheaper than existing solutions and work with any business worldwide."
    },
    {
      icon: AttachMoneyIcon,
      title: "How much does Sashy cost and what's the commitment?",
      description: "Plans start at $12/month for 1 location. No contracts, no commitments, no annual plans required. Pay monthly and cancel anytime. Start analyzing your reviews immediately."
    },
    {
      icon: BusinessIcon,
      title: "Can I analyze competitors and other businesses?",
      description: "Absolutely! Since we work with publicly available data, you can analyze any business worldwide - competitors, suppliers, clients, or businesses you're considering partnering with. No permissions or access needed."
    },
    {
      icon: CategoryIcon,
      title: "Which review platforms does Sashy analyze?",
        description: "We analyze reviews from Google, TripAdvisor, Trustpilot, Yelp, Booking.com, and for food & beverage businesses: JustEat, Deliveroo, UberEats, and Design My Night. We also offer custom integrations for any review platform. All platforms are analyzed automatically without you needing to connect or manage multiple accounts."
    },
    {
      icon: SecurityIcon,
      title: "Is there any risk in trying Sashy?",
      description: "Zero risk. We only use publicly available data, no account connections required. Pay monthly with no contracts or commitments. Cancel anytime if it doesn't work for your business."
    }
  ];

  const toggleExpanded = (index) => {
    const newExpandedItems = new Set(expandedItems);
    if (newExpandedItems.has(index)) {
      newExpandedItems.delete(index);
    } else {
      newExpandedItems.add(index);
    }
    setExpandedItems(newExpandedItems);
  };

  const FAQItem = ({ faq, index, isExpanded, onToggle }) => {
    const IconComponent = faq.icon;
    
    return (
      <Box sx={{
        p: 0,
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
        borderRadius: '16px',
        boxShadow: '0 8px 32px -12px rgba(0,0,0,0.1)',
        border: '1px solid rgba(25, 118, 210, 0.1)',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 12px 40px -12px rgba(0,0,0,0.15)'
        }
      }}>
        {/* Question Header - Clickable */}
        <Box
          onClick={onToggle}
          sx={{
            p: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            cursor: 'pointer',
            borderRadius: '16px',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.02)'
            }
          }}
          role="button"
          aria-expanded={isExpanded}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggle();
            }
          }}
        >
          <Box sx={{
            p: 1,
            borderRadius: '8px',
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 40,
            height: 40
          }}>
            <IconComponent sx={{ color: '#1976d2', fontSize: 20 }} />
          </Box>
          
          <Typography variant="h6" sx={{
            fontWeight: 700,
            color: '#0A2540',
            fontSize: '1.25rem',
            flex: 1
          }}>
            {faq.title}
          </Typography>
          
          <IconButton
            sx={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
              color: '#1976d2'
            }}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        {/* Answer - Collapsible */}
        <Collapse in={isExpanded} timeout={300}>
          <Box sx={{
            px: 4,
            pb: 4,
            pt: 0
          }}>
            <Typography variant="body1" sx={{
              color: '#546E7A',
              fontSize: '1.1rem',
              lineHeight: 1.6,
              ml: 7 // Align with the text above, accounting for icon width
            }}>
              {faq.description}
            </Typography>
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box id="faq-section" sx={{
      py: { xs: 8, md: 12 },
      background: 'linear-gradient(180deg, #f8f9fe 0%, #ffffff 100%)'
    }}>
      <Container maxWidth="md">
        <SectionHeader 
          title="Frequently Asked Questions"
        />
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              faq={faq}
              index={index}
              isExpanded={expandedItems.has(index)}
              onToggle={() => toggleExpanded(index)}
            />
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default FAQSection; 